import { isMacLike } from "@/utility/platformUtils";
import { t } from "@lingui/core/macro";
import { Duration } from "luxon";

export const UNDO_KEY = "Z";
export const REDO_KEY = "Y";
export const CTRL_KEY = "Control";
export const FORWARD_KEY = "L";
export const BACKWARD_KEY = "J";
export const CMD_KEY = "⌘ Command";
export const SHIFT_KEY = "Shift";

export type Shortcut = {
  default: readonly string[];
  mac?: readonly string[];
};

export const SHORTCUT_TRIGGERED_EVENT = "shortcutTriggered";

export enum SHORTCUTS {
  UNDO = "UNDO",
  REDO = "REDO",
  PLUS_5_MINUTES = "PLUS_5_MINUTES",
  MINUS_5_MINUTES = "MINUS_5_MINUTES",
  PLUS_ONE_HOUR = "PLUS_ONE_HOUR",
  MINUS_ONE_HOUR = "MINUS_ONE_HOUR",
  PLUS_ONE_DAY = "PLUS_ONE_DAY",
  MINUS_ONE_DAY = "MINUS_ONE_DAY",
  // TODO: Enable once toast messages work fine
  // AMBIENT_VOICE = "AMBIENT_VOICE",
}

export const SHORTCUT_DEFS = {
  UNDO: { default: [CTRL_KEY, UNDO_KEY], mac: [CMD_KEY, UNDO_KEY] },
  REDO: { default: [CTRL_KEY, SHIFT_KEY, REDO_KEY], mac: [CMD_KEY, SHIFT_KEY, REDO_KEY] },
  PLUS_5_MINUTES: { default: [FORWARD_KEY] },
  MINUS_5_MINUTES: { default: [BACKWARD_KEY] },
  PLUS_ONE_HOUR: { default: [CTRL_KEY, FORWARD_KEY] },
  MINUS_ONE_HOUR: { default: [CTRL_KEY, BACKWARD_KEY] },
  PLUS_ONE_DAY: { default: [CTRL_KEY, SHIFT_KEY, FORWARD_KEY] },
  MINUS_ONE_DAY: { default: [CTRL_KEY, SHIFT_KEY, BACKWARD_KEY] },
  // TODO: Enable once toast messages work fine
  // AMBIENT_VOICE: { default: [CTRL_KEY, "space"] },
} as const;

export type ShortcutKey = keyof typeof SHORTCUTS;

export const formatShortcut = (shortcut: Shortcut): string => {
  const keys = isMacLike && shortcut.mac ? shortcut.mac : shortcut.default;
  return keys.join(" + ");
};

const normalizeKey = (key: string): string => {
  const lowered = key.toLowerCase();
  if (lowered === " " || lowered === "spacebar" || lowered === "space") {
    return "space";
  }
  return lowered;
};

export const isShortcutTriggered = (event: KeyboardEvent, shortcut: Shortcut): boolean => {
  const targetKeys = (isMacLike && shortcut.mac ? shortcut.mac : shortcut.default).map(normalizeKey);

  const currentKeys = [
    ...(event.ctrlKey ? [CTRL_KEY] : []),
    ...(event.metaKey ? [CMD_KEY] : []),
    ...(event.shiftKey ? [SHIFT_KEY] : []),
    event.key,
  ].map(normalizeKey);

  return targetKeys.length === currentKeys.length && targetKeys.every((key) => currentKeys.includes(key));
};

export type TimeNavigationStep = {
  minutes: number;
  shortcut: SHORTCUTS;
  action: string;
  formattedShortcut: string;
};

export const TIME_NAVIGATION_STEPS: () => TimeNavigationStep[] = () => [
  {
    minutes: 5,
    shortcut: SHORTCUTS.PLUS_5_MINUTES,
    action: `+5 ${t`minutes`}`,
    formattedShortcut: formatShortcut(SHORTCUT_DEFS[SHORTCUTS.PLUS_5_MINUTES]),
  },
  {
    minutes: -5,
    shortcut: SHORTCUTS.MINUS_5_MINUTES,
    action: `-5 ${t`minutes`}`,
    formattedShortcut: formatShortcut(SHORTCUT_DEFS[SHORTCUTS.MINUS_5_MINUTES]),
  },
  {
    minutes: 60,
    shortcut: SHORTCUTS.PLUS_ONE_HOUR,
    action: `+1 ${t`hour`}`,
    formattedShortcut: formatShortcut(SHORTCUT_DEFS[SHORTCUTS.PLUS_ONE_HOUR]),
  },
  {
    minutes: -60,
    shortcut: SHORTCUTS.MINUS_ONE_HOUR,
    action: `-1 ${t`hour`}`,
    formattedShortcut: formatShortcut(SHORTCUT_DEFS[SHORTCUTS.MINUS_ONE_HOUR]),
  },
  {
    minutes: 24 * 60,
    shortcut: SHORTCUTS.PLUS_ONE_DAY,
    action: `+1 ${t`day`}`,
    formattedShortcut: formatShortcut(SHORTCUT_DEFS[SHORTCUTS.PLUS_ONE_DAY]),
  },
  {
    minutes: -(24 * 60),
    shortcut: SHORTCUTS.MINUS_ONE_DAY,
    action: `-1 ${t`day`}`,
    formattedShortcut: formatShortcut(SHORTCUT_DEFS[SHORTCUTS.MINUS_ONE_DAY]),
  },
];

export const getDurationFromMinutes = (minutes: number): Duration => {
  return Duration.fromObject({ minutes });
};
